import { getListName, productImpression } from 'analytics/enhanced-ecommerce'
import { productClickObject, productImpressionGenericObject, productImpressionObject, experienceImpressionObject, productClickGenericObject } from 'analytics/eventDefinitions'
import { productClick } from 'analytics/enhanced-ecommerce'
import * as Analytics from 'analytics/analytics'
import { clientEvent, searchEvent } from 'analytics/snowplow/events'
import { OfferListEvents } from 'components/OfferList/v2/OfferListEventsContext'
import { OFFER_TYPE_EXPERIENCE, OFFER_TYPE_HOTEL, PRODUCT_TYPE_TOUR, PRODUCT_TYPE_ULTRALUX, OFFER_TYPE_CRUISE } from 'constants/offer'
import uuidV4 from 'lib/uuidV4'
import { useCallback, useMemo } from 'react'
import { SearchEventData } from 'contexts/GlobalSearch/GlobalSearchTracking'
import { clickHomepageTileEvent, tpfmCarouselClickEvent, clickHomepageTrendingDestinationEvent } from 'analytics/optimizely/events'

type HomepageListType = 'tour' | 'hotel' | 'best_value_hotel' |'ultralux_hotel' | 'hero' | 'trending' | 'experience' | 'tpfm' | 'cruise'

export enum EventDataKey {
  ClientAction = 'client-action',
  ProductClick = 'product-click',
  ExperienceClick = 'experience-click',
  ProductImpression = 'product-impression',
  ExperienceImpression = 'experience-impression',
  TrendingImpression = 'trending-impression'
}

type ClientActionEventData = {
  key: EventDataKey.ClientAction;
  subject: string;
  action: string;
}

type ProductClickEventData = {
  key: EventDataKey.ProductClick;
  offer: App.AnyOffer;
  position: number;
}

type ExperienceClickEventData = {
 key: EventDataKey.ExperienceClick;
 experience: App.ExperienceOffer;
 position: number;
}

type ProductImpressionEventData = {
  key: EventDataKey.ProductImpression;
  offer: App.AnyOffer;
  position: number;
}

type ExperienceImpressionEventData = {
  key: EventDataKey.ExperienceImpression;
  experience: App.ExperienceBookingView;
  position: number;
}

type TrendingImpressionEventData = {
  key: EventDataKey.TrendingImpression;
  destination: string;
  position: number;
}

type ClientActionEvent = {
  action: OfferListEvents.clientAction;
  data: ClientActionEventData;
}

type ProductClickEvent = {
  action: OfferListEvents.productClick;
  data: ProductClickEventData;
}

type ExperienceClickEvent = {
  action: OfferListEvents.productClick;
  data: ExperienceClickEventData;
}

type ProductImpressionEvent = {
  action: OfferListEvents.impression;
  data: ProductImpressionEventData;
}

type ExperienceImpressionEvent = {
  action: OfferListEvents.impression;
  data: ExperienceImpressionEventData;
}

type TrendingImpressionEvent = {
  action: OfferListEvents.impression;
  data: TrendingImpressionEventData;
}

type Event = ClientActionEvent | ProductClickEvent | ExperienceClickEvent | ProductImpressionEvent | ExperienceImpressionEvent | TrendingImpressionEvent
type Data = ClientActionEventData | ProductClickEventData | ExperienceClickEventData | ProductImpressionEventData | ExperienceImpressionEventData | TrendingImpressionEventData

/**
  * @remarks
  * We check the key of the data param in order to identify the typescript type of data, as the type Data is a union type so we need to narrow this down
  */
function buildEvent(action: OfferListEvents, data: Data): Event | null {
  if (action === OfferListEvents.productClick && data.key === EventDataKey.ProductClick) {
    return { action, data }
  } else if (action === OfferListEvents.productClick && data.key === EventDataKey.ExperienceClick) {
    return { action, data }
  } else if (action === OfferListEvents.clientAction && data.key === EventDataKey.ClientAction) {
    return { action, data }
  } else if (action === OfferListEvents.impression && data.key === EventDataKey.ProductImpression) {
    return { action, data }
  } else if (action === OfferListEvents.impression && data.key === EventDataKey.ExperienceImpression) {
    return { action, data }
  } else if (action === OfferListEvents.impression && data.key === EventDataKey.TrendingImpression) {
    return { action, data }
  }
  return null
}

function useHomepageAnalytics(version: 'homepage' | 'old-homepage', currency: string) {
  const listParams = useMemo(() => {
    return {
      [PRODUCT_TYPE_TOUR]: { listId: uuidV4(), listName: getListName('OfferList', version, 'tour-carousel') },
      [OFFER_TYPE_HOTEL]: { listId: uuidV4(), listName: getListName('OfferList', version, 'hotel-carousel') },
      [PRODUCT_TYPE_ULTRALUX]: { listId: uuidV4(), listName: getListName('OfferList', version, 'ultralux-carousel') },
      [OFFER_TYPE_EXPERIENCE]: { listId: uuidV4(), listName: getListName('OfferList', version, 'experience-carousel') },
      tpfm: { listId: uuidV4(), listName: getListName('OfferList', version, 'tpfm-carousel') },
      hero: { listId: uuidV4(), listName: getListName('OfferList', version, 'hero-carousel') },
      trending: { listId: uuidV4(), listName: `${version}-trending-destinations` },
    }
  }, [version])

  const onListEvent = useCallback((productType: HomepageListType, event: Event) => {
    const params = listParams[productType]
    const { action, data } = event
    if (data.key === EventDataKey.ProductClick || data.key === EventDataKey.ExperienceClick) {
      Analytics.track('click-homepage-tile', clickHomepageTileEvent())
    }

    if (params) {
      if (action === OfferListEvents.productClick && data.key === EventDataKey.ProductClick) {
        const obj = productClickObject(data.offer, data.position + 1)
        productClick(currency, obj, params)
      } else if (action === OfferListEvents.productClick && data.key === EventDataKey.ExperienceClick) {
        const obj = productClickGenericObject({
          id: data.experience.id,
          name: data.experience.name,
          category: 'experiences_offer',
          price: data.experience.price,
          position: data.position + 1,
        })
        productClick(currency, obj, params)
      } else if (action === OfferListEvents.clientAction) {
        Analytics.track('client-event', clientEvent(data.subject, data.action, params.listName, 'interaction'))
      } else if (action === OfferListEvents.impression && data.key === EventDataKey.ProductImpression) {
        const impressionObj = productImpressionObject(data.offer, params.listName, data.position + 1)
        productImpression(currency, [impressionObj], params)
      } else if (action === OfferListEvents.impression && data.key === EventDataKey.ExperienceImpression) {
        const impressionObj = experienceImpressionObject(data.experience, params.listName, data.position + 1)
        productImpression(currency, [impressionObj], params)
      } else if (action === OfferListEvents.impression && data.key === EventDataKey.TrendingImpression) {
        const trendingObjParams = {
          id: data.destination,
          name: data.destination,
          position: data.position + 1,
        }
        const trendingImpressionObject = productImpressionGenericObject(trendingObjParams)
        productImpression(currency, [trendingImpressionObject], params)
      }
    }
  }, [currency, listParams])

  const onTourListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      onListEvent(PRODUCT_TYPE_TOUR, event)
    }
  }, [onListEvent])

  const onCruiseListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) { onListEvent(OFFER_TYPE_CRUISE, event) }
  }, [onListEvent])

  const onHotelListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      onListEvent(OFFER_TYPE_HOTEL, event)
    }
  }, [onListEvent])

  const onBestValueHotelListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      onListEvent('best_value_hotel', event)
    }
  }, [onListEvent])

  const onUltraLuxListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      onListEvent(PRODUCT_TYPE_ULTRALUX, event)
    }
  }, [onListEvent])

  const onHeroListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      onListEvent('hero', event)
    }
  }, [onListEvent])

  const onSearchEvent = useCallback((subject: string, data: SearchEventData) => {
    Analytics.track('search-event', searchEvent(subject, version, data))
  }, [version])

  const onTrendingDestinationsListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      onListEvent('trending', event)
      if (event.action === OfferListEvents.clientAction) {
        Analytics.track('click-homepage-trending-destinations-event', clickHomepageTrendingDestinationEvent())
      }
    }
  }, [onListEvent])

  const onExperienceListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      onListEvent(OFFER_TYPE_EXPERIENCE, event)
    }
  }, [onListEvent])

  const onTPFMListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) {
      if (event.action === OfferListEvents.productClick) {
        Analytics.track('click-tpfm-carousel', tpfmCarouselClickEvent())
      }

      onListEvent('tpfm', event)
    }
  }, [onListEvent])

  return {
    onTourListEvent,
    onCruiseListEvent,
    onHotelListEvent,
    onBestValueHotelListEvent,
    onUltraLuxListEvent,
    onHeroListEvent,
    onSearchEvent,
    onTrendingDestinationsListEvent,
    onExperienceListEvent,
    onTPFMListEvent,
  }
}

export default useHomepageAnalytics
